class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;

    this.onOverlayClick = this.close.bind(this);
    this.onKeyUp = (evt) => evt.code === 'Escape' && this.close();

    // ✅ debounce p/ evitar spam de eventos
    this.__apxMutRAF = null;

    // ✅ flag p/ evitar postRender duplicado (renderContents -> open)
    this.__apxJustRendered = false;

    this.addEventListener('keyup', this.onKeyUp);

    // bind inicial
    this.attachOverlayListener();
    this.setHeaderCartIconAccessibility();

    // garante acessibilidade do summary quando existir
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
  }

  // =========================
  // Helpers
  // =========================
  notifyCartMutated() {
    // ✅ 1 evento por frame (evita loop e “Carregando…” eterno)
    if (this.__apxMutRAF) return;

    this.__apxMutRAF = requestAnimationFrame(() => {
      this.__apxMutRAF = null;
      try {
        window.dispatchEvent(new CustomEvent('apx:cart-mutated'));
      } catch (e) {}
    });
  }

  attachOverlayListener() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    // evita duplicar listener quando o drawer re-renderiza
    if (overlay.__apx_bound_overlay) return;
    overlay.__apx_bound_overlay = true;

    overlay.addEventListener('click', this.onOverlayClick);
  }

  updateEmptyState(parsedState) {
    if (!parsedState || typeof parsedState.item_count !== 'number') return;

    const isEmpty = parsedState.item_count === 0;

    this.classList.toggle('is-empty', isEmpty);

    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', isEmpty);
  }

  retrapFocusIfActive() {
    if (!this.classList.contains('active')) return;

    try {
      removeTrapFocus(this.activeElement);
    } catch (e) {}

    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');

    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

    if (containerToTrapFocusOn && focusElement) {
      trapFocus(containerToTrapFocusOn, focusElement);
    }
  }

  /**
   * ✅ Rodar sempre após qualquer render/replace de HTML
   */
  postRender(parsedState) {
    this.attachOverlayListener();
    this.setHeaderCartIconAccessibility();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.updateEmptyState(parsedState);
    this.retrapFocusIfActive();

    // ✅ debounced
    this.notifyCartMutated();
  }

  // =========================
  // Header Icon
  // =========================
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    // header pode ser re-renderizado e perder listeners
    if (cartLink.__apx_cartdrawer_bound) return;
    cartLink.__apx_cartdrawer_bound = true;

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // =========================
  // Open / Close
  // =========================
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');

      // ✅ evita postRender duplicado quando renderContents acabou de rodar
      if (this.__apxJustRendered) {
        this.__apxJustRendered = false;
      } else {
        this.postRender();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    try {
      removeTrapFocus(this.activeElement);
    } catch (e) {}
    document.body.classList.remove('overflow-hidden');
  }

  // =========================
  // Accessibility Summary
  // =========================
  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // =========================
  // Sections Rendering
  // =========================
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner?.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // ✅ importante: roda pós-render depois que DOM novo já existe
    setTimeout(() => {
      this.postRender(parsedState);

      // ✅ marca que acabou de renderizar, pra open() não chamar postRender de novo
      this.__apxJustRendered = true;

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * ✅ update/remove:
   * - super.renderContents troca a .drawer__inner inteira
   * - então o pós-render precisa rodar depois do DOM novo existir
   * - e SEM spammar eventos (senão progressivo fica em “Carregando…”)
   */
  renderContents(parsedState) {
    super.renderContents(parsedState);

    // roda no próximo frame p/ garantir que o DOM novo já foi inserido
    requestAnimationFrame(() => {
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.postRender === 'function') {
        drawer.postRender(parsedState);
      } else {
        // fallback: pelo menos atualiza o progressivo, mas com debounce via RAF
        try {
          window.dispatchEvent(new CustomEvent('apx:cart-mutated'));
        } catch (e) {}
      }
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
